'use client'
import React, { useEffect, useState, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  IconTwitter,
  IconSpinner,
  IconInstagram,
  IconTiktok,
  IconFacebook,
  IconPinterest,
} from '@/components/ui/icons';
import {
  IconEdit,
  IconInfo,
} from '@/components/ui/icons-v2';
import Image from 'next/image';
import { toast } from '@/lib/toast';
import useUserStore from '@/lib/hooks/zustand-stores/use-user-store';
import { notifyReferrer } from '@/app/actions/emails';
import { Checkbox } from '@/components/ui/checkbox';
import { SocialSearchResult, AuthorBio } from '@/lib/types';
import { isProfileUrl, parseSummary } from '@/lib/utils';
import { capitalize } from 'lodash';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import ProfilePic from '../user/profile-image';
import { LinkedInLogoIcon } from '@radix-ui/react-icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import useTourStore from '@/lib/hooks/zustand-stores/use-tour-store';
import { useLogger } from '@/lib/hooks/useLogger';
import { Dialog } from '@/components/ui/dialog'
import { ContentModerationDialog } from '@/components/content-moderation-dialog'
import { usePostHogEvent } from '@/lib/hooks/use-posthog'
import Interests from './interests';
import { saveNewProfilePicture } from '@/app/actions/images';
import { searchSocialProfile, syncSocialProfile, summarizeSocialData, fetchAndSaveSocialData } from '@/app/actions/social';
import { updateUserBio, updateUser } from '@/app/actions/users';
import { useSocialSearchProgress } from '@/lib/hooks/use-social-search-progress';
import useWebSocketStore from '@/lib/hooks/zustand-stores/use-websocket-store'
import DemoRequestDialog from '../demo-request-dialog';
import { getAppUrl } from '@/lib/utils/url';
import { MessageSquareCode, Users2, Building2, Shield } from 'lucide-react';
import { AuroraText } from '@/components/ui/aurora-text';

const Onboarding: React.FC = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user } = useUser();
  const [currentStep, setCurrentStep] = useState(0);
  const [alternativeSocialUrl, setAlternativeSocialUrl] = useState('');
  const [selectedAlternativeSocialProfile, setSelectedAlternativeSocialProfile] = useState<any>(null);
  const [hasSocialMedia, setHasSocialMedia] = useState(true);
  // const [manualAboutMe, setManualAboutMe] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('Creating your mixus profile...');
  const syncMessages = [
    'Creating your mixus profile...',
    'Gathering your data...',
    'Analyzing your profile...',
    'Automagically creating profile...'
  ];
  const [syncingBg, setSyncingBg] = useState(false);
  const mixusUserId = useUserStore(state => state.user?._id);
  const mixusClerkUserId = useUserStore(state => state.user?.clerkUserId);
  const mixusClerkHeadline = useUserStore(state => state.user?.bio?.headline);
  const mixusUserFirstName = useUserStore(state => state.user?.firstName);
  const mixusUserLastName = useUserStore(state => state.user?.lastName);
  const mixusUserFullName = `${mixusUserFirstName ?? ''} ${mixusUserLastName ?? ''}`.trim();
  const mixusUsername = useUserStore(state => state.user?.username);
  const mixusUserReferredBy = useUserStore(state => state.user?.referredBy);
  const [selectedTopics, setSelectedTopics] = useState<{ name: string, subtopics: string[] }[]>([]);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [generatedAboutMe, setGeneratedAboutMe] = useState('');
  const [consentCheck, setConsentCheck] = useState(false);
  const [firstName, setFirstName] = useState<string | null>(null);
  // const [lastName, setLastName] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string>('linkedin');
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkedInSearching, setIsLinkedInSearching] = useState(false);
  const [searchResultsByPlatform, setSearchResultsByPlatform] = useState<{ [key: string]: SocialSearchResult[] }>({});
  const [viewProfile, setViewProfile] = useState(false);
  const isTourCompleted = useTourStore(state => state.tourCompleted);
  const updateUserData = useUserStore(state => state.updateUserData)
  const [headline, setHeadline] = useState('');
  const [location, setLocation] = useState({ city: '', state: '', country: '' });
  const [employer, setEmployer] = useState('');
  const [school, setSchool] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [tourDone, setTourDone] = useState(false);
  const [hasInitialSearchRun, setHasInitialSearchRun] = useState(false);
  const initialSearchRef = useRef(false);
  const [profilePicture, setProfilePicture] = useState('')
  const [savingProfilePic, setSavingProfilePic] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [placesKnownWell, setPlacesKnownWell] = useState<string[]>([]);
  const setTourStarted = useTourStore(state => state.setTourStarted);
  const logger = useLogger();
  const referral = new URLSearchParams(window.location.search).get('ref')
  const captureEvent = usePostHogEvent()
  const [isSaving, setIsSaving] = useState(false);
  const connectionStatus = useWebSocketStore(state => state.connectionStatus);
  const searchProgress = useSocialSearchProgress(mixusUserId || '');

  const searchMessages = [
    'Searching for profiles...',
    'Fetching profiles...',
    'Analyzing data...',
    'Finalizing search...'
  ];

  const [searchMessageIndex, setSearchMessageIndex] = useState(0);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const rotateSearchMessage = () => {
      setSearchMessageIndex((prev) => (prev + 1) % searchMessages.length);
    };

    if ((isLoading || isLinkedInSearching) && connectionStatus !== 'connected') {
      timeoutId = setTimeout(rotateSearchMessage, 2000);
    }

    return () => clearTimeout(timeoutId);
  }, [isLoading, isLinkedInSearching, searchMessageIndex, connectionStatus]);
  const [showDemoDialog, setShowDemoDialog] = useState(false);

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const closeModal = () => {
    setTourDone(true);
    useTourStore.getState().setTourCompleted(true);

    // Use a small delay to ensure state updates are processed
    setTimeout(() => {
      setShowOnboarding(false);
      if (!tourDone) {
        router.replace('/organizations');
      }
    }, 0);
  };

  const finishOnboarding = async () => {
    setIsSaving(true);
    try {
      if (!isSyncing && mixusUserId) {
        // Update the bio in the database
        const bioUpdateResult = await updateUserBio(mixusUserId, bioData);

        if (bioUpdateResult?.flagged) {
          setModerationError(bioUpdateResult.message);
          setShowModerationDialog(true);
          return;
        }

        if (!bioUpdateResult) {
          throw new Error('Failed to save profile summary');
        }

        // Update the local userStore
        updateUserData({
          _id: mixusUserId,
          bio: bioData,
          imageUrl: profilePicture || undefined,
          followedTopics: selectedTopics
        });

        // Show success toast and dismiss modal
        //toast.success('Profile and interests saved successfully');
        closeModal();
      } else {
        // If no saving is needed, just dismiss the modal
        closeModal();
      }
    } catch (error) {
      logger.error('Error saving profile or interests:', error);
      toast.error('Failed to save your profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Create the complete bio object with all fields
  const bioData: Partial<AuthorBio> = {
    aboutMe: generatedAboutMe,
    headline,
    location: {
      city: location.city,
      state: location.state,
      country: location.country
    },
    employer,
    school,
    interests,
    placesKnownWell
  };

  const [searchStatus, setSearchStatus] = useState('Searching...');

  // Single Global State for Showing & Hiding Onboarding
  const showOnboarding = useTourStore(state => state.showOnboarding);
  const setShowOnboarding = useTourStore(state => state.setShowOnboarding);

  const [showModerationDialog, setShowModerationDialog] = useState(false);
  const [moderationError, setModerationError] = useState<string>('');

  useEffect(() => {
    let messageIndex = 0;
    let timeoutId: NodeJS.Timeout;

    const changeMessage = () => {
      setSyncStatus(syncMessages[messageIndex]);
      messageIndex = (messageIndex + 1) % syncMessages.length;

      // Set a new timeout with a random duration between 1 and 3 seconds
      const randomInterval = Math.floor(Math.random() * 2000) + 1000;
      timeoutId = setTimeout(changeMessage, randomInterval);
    };

    if (isSyncing) {
      changeMessage();
    }

    return () => clearTimeout(timeoutId);
  }, [isSyncing]);

  useEffect(() => {
    logger.info('searchResultsByPlatform updated:', searchResultsByPlatform);
  }, [searchResultsByPlatform]);

  useEffect(() => {
    logger.info('Current platform:', platform);
  }, [platform]);

  useEffect(() => {
    if (showOnboarding && fullName && !initialSearchRef.current) {
      initialSearchRef.current = true;
      handleSearchSocialProfile('linkedin');
      setHasInitialSearchRun(true);
    }
  }, [showOnboarding, fullName]);

  const handleSearchSocialProfile = async (platform: string) => {
    if (!fullName) {
      console.log('No full name found');
      return;
    }

    const startTime = Date.now(); // Start timing the search
    logger.info(`Search started for ${platform} at ${new Date(startTime).toLocaleTimeString()}`);

    if (platform === 'linkedin') {
      setIsLinkedInSearching(true);
    } else {
      setIsLoading(true);
    }

    setSearchInitiated(true);
    try {
      const data = await searchSocialProfile(fullName, platform);
      logger.info(`Search results for ${platform}:`, data);

      if (data === undefined) {
        setSearchResultsByPlatform(prev => ({ ...prev, [platform]: [] }));
      } else {
        setSearchResultsByPlatform(prev => ({ ...prev, [platform]: data as SocialSearchResult[] }));
      }

      const endTime = Date.now(); // End timing the search
      const durationInSeconds = ((endTime - startTime) / 1000).toFixed(2);
      logger.info(`Search completed in ${durationInSeconds} seconds`);
    } catch (error) {
      logger.error(`Error searching ${platform} profile:`, error);
      /* toast.error(`Could not find any results, please paste your social media URL instead`); */
      setSearchResultsByPlatform(prev => ({ ...prev, [platform]: [] }));
    } finally {
      if (platform === 'linkedin') {
        setIsLinkedInSearching(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const onboard = searchParams?.get('onboard');
    const forceOnboard = searchParams?.get('force_onboard');

    if ((forceOnboard === 'true' || (onboard === 'true' && !mixusClerkHeadline && !tourDone))) {
      // Disable showing the onboarding dialog
      // setShowOnboarding(true);
      setTourStarted(true);
      captureEvent('onboarding_started', {
        trigger: forceOnboard === 'true' ? 'force_onboard_param' : 'onboard_param',
        userId: mixusUserId,
        clerkUserId: mixusClerkUserId,
        firstName: mixusUserFirstName,
        hasHeadline: !!mixusClerkHeadline
      });
    }
  }, [mixusClerkUserId, searchParams, mixusClerkHeadline, user?.id, setShowOnboarding, setTourStarted, tourDone, captureEvent, mixusUserId, mixusUserFirstName]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || null);
      setFullName(`${user.firstName} ${user.lastName}` || null);
      setProfilePicture(user.imageUrl || '');
    }
  }, [user]);

  const handleAlternativeSocialInput = async () => {
    if (!alternativeSocialUrl) {
      toast.error('Please enter a valid social media URL');
      return;
    }

    const platform = getPlatformFromUrl(alternativeSocialUrl);
    if (platform === 'unknown') {
      toast.error('Please enter a valid LinkedIn, Twitter, Instagram, Facebook, TikTok, or Pinterest URL');
      return;
    }

    setIsSyncing(true);
    try {
      await handleSyncProfile(alternativeSocialUrl, platform);
    } catch (error) {
      logger.error('Error syncing alternative social profile:', error);
      /* toast.error('Failed to connect social profile. Please try again.'); */
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncProfile = async (url: string, platform: string) => {
    // Get the selected profile data from searchResultsByPlatform
    const selectedProfile = searchResultsByPlatform[platform]?.find(
      profile => profile.url === url
    );

    if (!isProfileUrl(url, platform)) {
      toast.error(`Please enter a valid ${capitalize(platform)} URL`);
      return;
    }

    if (platform !== 'linkedin') {
      toast.success(`Analyzing your ${platform} profile...`);
    }
    setIsSyncing(true);
    const syncStartTime = Date.now();
    logger.info(`Sync started for ${platform} at ${new Date(syncStartTime).toLocaleTimeString()}`);
    setSyncStatus(`Creating your mixus profile...`);

    try {
      const result = await syncSocialProfile(url, platform);

      if (!result.success) {
        // Use fallback data if available
        if (selectedProfile) {
          const fallbackData = {
            title: selectedProfile.title || '',
            description: selectedProfile.description || '',
            platform: platform
          };

          const summaryResult = await summarizeSocialData([fallbackData], platform);
          if (summaryResult.success) {
            const bioData: Partial<AuthorBio> = parseSummary(summaryResult.summary || '');

            setGeneratedAboutMe(bioData.aboutMe || '');
            setHeadline(bioData.headline || '');
            setLocation({
              city: bioData.location?.city || '',
              state: bioData.location?.state || '',
              country: bioData.location?.country || ''
            });

            if (!mixusUserId) {
              throw new Error('User ID is not available');
            }

            const updateResult = await updateUserBio(mixusUserId, bioData);
            if (updateResult?.flagged) {
              setModerationError(updateResult.message);
              setShowModerationDialog(true);
              setShowOnboarding(true);
              return;
            }

            if (!updateResult) {
              throw new Error('Failed to save profile summary');
            }

            toast.success('Created profile with limited data');
            setCurrentStep(3);
            return;
          }
        }

        setCurrentStep(3);
        setIsSyncing(false);
        throw new Error(result.error || `Failed to sync ${platform} profile`);
      }

      logger.info(`${platform} sync initiated. Result:`, result);

      if (result.data.snapshot_id && mixusUserId) {
        logger.info(`Starting data fetch for ${platform}. Snapshot ID:`, result.data.snapshot_id);
        let retries = 0;
        const maxRetries = 12; // 12 attempts * 10 seconds = 2 minutes total
        const retryDelay = 10000; // 10 seconds between attempts
        let dataResult;
        let syncBg: boolean = true;

        while (retries < maxRetries) {
          if (syncBg) {
            finishOnboarding();
            setTimeout(() => {
              toast.loading('Your mixus profile is being created by AI', {
                dismissible: !isSyncing,
                duration: Infinity,
                id: 'syncing'
              })
              syncBg = false
              setSyncingBg(true)
            }, 1000)
          }

          await new Promise(resolve => setTimeout(resolve, retryDelay));

          try {
            dataResult = await fetchAndSaveSocialData(result.data.snapshot_id, mixusUserId, platform, mixusClerkUserId);
            logger.info(`Fetch attempt ${retries + 1} for ${platform}. Result:`, dataResult);

            if (!dataResult.success) {
              throw new Error(dataResult.error || `Failed to fetch ${platform} data`);
            }

            if (dataResult.data.status !== 'running') {
              if (Array.isArray(dataResult.data) && dataResult.data.length === 0) {
                throw new Error(
                  `Could not create your profile with ${capitalize(platform)}, please try another social media profile`
                )
              }

              logger.info(`${platform} data ready. Proceeding with summarization.`);
              setSyncStatus('Summarizing your profile...');

              if (dataResult.image_url) {
                const image = await saveNewProfilePicture(mixusUserId)
                setProfilePicture(image?.imageUrl)
              }

              const dataToSummarize = Array.isArray(dataResult.data) ? dataResult.data : [dataResult.data];

              const summaryResult = await summarizeSocialData(dataToSummarize, platform);
              if (!summaryResult.success) {
                throw new Error(summaryResult.error || 'Failed to summarize profile');
              }

              setSyncStatus('Saving your profile summary...');
              const bioData: Partial<AuthorBio> = parseSummary(summaryResult.summary || '');
              // logger.info("bioData>>>", bioData)

              setGeneratedAboutMe(bioData.aboutMe || '');
              setHeadline(bioData.headline || '');
              setLocation({
                city: bioData.location?.city || '',
                state: bioData.location?.state || '',
                country: bioData.location?.country || ''
              });
              setEmployer(bioData.employer || '');
              setSchool(bioData.school || '');
              setPlacesKnownWell(bioData.placesKnownWell || []);

              // Update the bio in the database
              const bioUpdateResult = await updateUserBio(mixusUserId, bioData);
              if (bioUpdateResult?.flagged) {
                setModerationError(bioUpdateResult.message);
                setShowModerationDialog(true);
                setShowOnboarding(true);
                return;
              }

              if (!bioUpdateResult) {
                throw new Error('Failed to save profile summary');
              }

              // No more saving the bio in clerk metadata
              // if (mixusClerkUserId) {
              //   await updateClerkPrivateMetadata(
              //     mixusClerkUserId,
              //     'bio',
              //     bioData
              //   );
              // }

              // Update the local userStore
              updateUserData({
                _id: mixusUserId,
                bio: bioData,
                imageUrl: profilePicture || undefined,
                followedTopics: selectedTopics
              });

              logger.info(`${platform} profile sync completed successfully.`);
              toast.dismiss('syncing');

              const aboutMeStepIndex = steps.findIndex(step => step.title === 'mixus profile');
              if (aboutMeStepIndex !== -1 && syncBg) {
                toast.success(`Your mixus profile is ready for your review`, {
                  duration: Infinity,
                  action: {
                    label: 'View',
                    onClick: () => {
                      setCurrentStep(aboutMeStepIndex);
                      setSyncingBg(false);
                      setShowOnboarding(true);
                    }
                  }
                });
              } else {
                setViewProfile(true)
              }
              const syncEndTime = Date.now();
              const syncDurationInSeconds = ((syncEndTime - syncStartTime!) / 1000).toFixed(2);
              logger.info(`Sync completed in ${syncDurationInSeconds} seconds`);

              const aiProfileStartTime = Date.now();
              logger.info(`AI profile creation started at ${new Date(aiProfileStartTime).toLocaleTimeString()}`);

              const aiProfileEndTime = Date.now();
              const aiProfileDurationInSeconds = ((aiProfileEndTime - aiProfileStartTime) / 1000).toFixed(2);
              logger.info(`AI profile creation completed in ${aiProfileDurationInSeconds} seconds`);

              return;
            }
          } catch (fetchError: any) {
            logger.error(`Error in fetch attempt ${retries + 1}:`, fetchError);
            if (fetchError.message.includes('API error: 400 Bad Request')) {
              throw new Error(`Could not get ${platform} data: Please try using another social media profile or fill out your profile manually.`);
            }
            if (fetchError.message.includes('Could not create your profile with')) {
              throw new Error(
                `Could not create your profile with ${capitalize(platform)}, please try another social media profile`
              )
            }
            if (retries === maxRetries - 1) {
              logger.error(`Fetch attempt ${retries + 1} for ${platform} timed out after ${maxRetries} attempts`);
              toast.dismiss('syncing');
              toast.error(`Failed to fetch ${capitalize(platform)} data. Please input your profile info manually.`, {
                duration: Infinity,
                action: {
                  label: 'View',
                  onClick: () => {
                    setCurrentStep(2);
                    setSyncingBg(false);
                    setShowOnboarding(true);
                  },
                }
              });
              throw fetchError; // Throw on the last retry
            }
          }

          if (retries === maxRetries) {
            setCurrentStep(3);
            setSyncingBg(false);
            setShowOnboarding(true);
            throw new Error(`Profile sync timed out after ${maxRetries * (retryDelay/1000)} seconds`);
          }
          retries++;
        }
      } else {
        throw new Error(`Failed to get snapshot ID for ${platform} profile or user ID is not available`);
      }
    } catch (error: any) {
      logger.error(`Error syncing ${platform} profile:`, error);
      if (error.message.includes("profile appears to be private or inaccessible") || error.message.includes("Failed to check profile visibility")) {
        toast.error(`This ${capitalize(platform)} profile is private. Please fill out your profile manually.`, {duration: 4000});
        setCurrentStep(currentStep + 1);
      } else {
        toast.dismiss('syncing');
        toast.error(`This ${capitalize(platform)} profile is private. Please fill out your profile manually.`, {duration: 4000});
        setCurrentStep(currentStep + 1);
      }
    } finally {
      setIsSyncing(false);
      setSyncStatus('');
    }
  };

  useEffect(() => {
    if (viewProfile && isTourCompleted) {
      toast.success(`Your mixus profile is ready for your review`, {
        duration: Infinity,
        dismissible: false,
        action: {
          label: 'View',
          onClick: () => {
            setCurrentStep(3);
            setSyncingBg(false);
            setShowOnboarding(true);
          }
        }
      });
    }
  }, [viewProfile, isTourCompleted]);
  const appUrl = getAppUrl()
  const shareOnFacebook = () => {
    const url = encodeURIComponent(appUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
  }

  const shareOnX = () => {
    const text = encodeURIComponent('Join me at mixus.ai, where you can chat with both AI and people simultaneously!');
    const url = encodeURIComponent(appUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}%20${url}`, '_blank');
  }

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(appUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  }

  const getPlatformFromUrl = (url: string): string => {
    if (url.includes('facebook.com')) return 'facebook';
    if (url.includes('x.com')) return 'twitter';
    if (url.includes('pinterest.com')) return 'pinterest';
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('tiktok.com')) return 'tiktok';
    if (url.includes('linkedin.com')) return 'linkedin';
    return 'unknown';
  };

  const nextStep = async () => {
    // Track step completion
    captureEvent('onboarding_step_completed', {
      step: currentStep,
      stepName: steps[currentStep].title,
      userId: mixusUserId,
      clerkUserId: mixusClerkUserId,
      totalSteps: steps.length
    });

    if (currentStep === 0) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 1) {
      // Move to next step without requiring social media URL
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 2) {
      if (hasSocialMedia && !alternativeSocialUrl && !selectedAlternativeSocialProfile) {
        toast.error('Please enter a social media URL or select "Manually create profile"');
        return;
      }

      if (!hasSocialMedia) {
        // If manually creating profile, move to the profile creation step
        setCurrentStep(3);
      } else {
        finishOnboarding();
      }
    } else if (currentStep === 3) {
      // Validate required fields before finishing
      if (!headline.trim()) {
        toast.error('Please enter a headline');
        return;
      }
      if (!generatedAboutMe.trim()) {
        toast.error('Please tell us a bit about yourself so we can connect you with relevant content');
        return;
      }

      // Call finishOnboarding to save the profile
      await finishOnboarding();
    }
  };

  const handleUpdateProfilePic = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setSavingProfilePic(true)
      if (e.target.files) {
        await user?.setProfileImage({
          file: e.target.files[0]
        })
        const image = await saveNewProfilePicture(mixusUserId!)
        setProfilePicture(image?.imageUrl)
        updateUserData({imageUrl: image?.imageUrl})
        toast.success('Profile photo updated successfully')
      }
    } catch (error) {
      logger.info('Error Saving Profile Picture', error)
      toast.error('Failed to update profile photo')
    } finally {
      setSavingProfilePic(false)
    }
  }

  const steps = [
    {
      title: (
        <h2 className="text-2xl sm:text-3xl font-medium tracking-tight flex flex-wrap items-center gap-x-2 mb-1">
          <span className="text-black dark:text-white">Welcome to</span>
          <div className="flex items-center gap-x-2">
            <AuroraText className="font-bold text-2xl sm:text-3xl">
              mixus
            </AuroraText>
            <span className="bg-gradient-to-r from-[#4F46E5] via-[#9333EA] to-[#60A5FA] text-transparent bg-clip-text font-semibold text-2xl sm:text-3xl whitespace-nowrap">
              for business
            </span>
          </div>
        </h2>
      ),
      description: (
        <div className="space-y-6">
          <div className="bg-[#F8FAFC] dark:bg-slate-900/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-800">
            <p className="text-[#475569] dark:text-gray-300 text-base leading-relaxed mb-6">
              Experience collaborative AI that understands your team's context, data, and conversations in real-time. Connect with colleagues, leverage AI insights, and work smarter together.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="text-[#4F46E5] dark:text-[#818CF8]">
                  <MessageSquareCode className="w-5 h-5" />
                </div>
                <p className="text-[#334155] dark:text-white text-base">Collaborative AI Chat</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-[#9333EA] dark:text-[#C084FC]">
                  <Users2 className="w-5 h-5" />
                </div>
                <p className="text-[#334155] dark:text-white text-base">Colleague Connect</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-[#60A5FA] dark:text-[#93C5FD]">
                  <Building2 className="w-5 h-5" />
                </div>
                <p className="text-[#334155] dark:text-white text-base">Live Data Hub</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-[#475569] dark:text-gray-300 text-base">
              Ready to transform your company's workspace?
            </p>
            <Button
              variant="outline"
              className="w-full h-12 text-base font-medium rounded-lg border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={() => window.open('https://calendly.com/shai-mixus/30min', '_blank')}
            >
              Book a call
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: `Welcome${firstName ? `, ${firstName}` : ''}!`,
      description: (
        <>
          <div className="space-y-4">
            <p>
              AI will create your profile in about 1-2 minutes. You can manually edit it anytime afterward.
            </p>
            <p>
              The more info your profile has, the better mixus can connect you with other people in your organization.
            </p>
          </div>
        </>
      ),
    },
    {
      title: 'Create Your Profile with AI',
      description: (
        <>
          Paste a link to your LinkedIn profile and AI will create your mixus profile.
        </>
      ),
      content: (
        <div className="space-y-4 px-2">
          {/* <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 mb-4">
            <p className="text-sm text-yellow-700 dark:text-yellow-200">
              <strong>Note:</strong> If your social media profiles are private or protected, our system will detect it and prompt you to manually create your mixus profile on the next screen.
            </p>
          </div> */}
          <div className="my-1">
            <input
              type="text"
              value={alternativeSocialUrl}
              onChange={(e) => setAlternativeSocialUrl(e.target.value)}
              placeholder="Social media URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white mb-2"
            />
          </div>
          {/* <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 font-semibold bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or search for one of your social media profiles</span>
            </div>
          </div>
          <input
            type="text"
            value={fullName || ''}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Full Name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
          <div className="relative">
            <Select
              value={platform}
              onValueChange={(e) => {
                setPlatform(e);
                // Only search if we don't already have results for this platform
                if (!searchResultsByPlatform[e] || searchResultsByPlatform[e]?.length === 0) {
                  setSearchResultsByPlatform(prev => ({ ...prev, [e]: [] })); // Clear previous results
                  handleSearchSocialProfile(e);
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Platform" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800">
                <SelectItem value='linkedin'>
                  <div className="flex items-center space-x-3">
                    <LinkedInLogoIcon className="text-blue-600 w-6 h-6" />
                    <span>LinkedIn</span>
                  </div>
                </SelectItem>
                <SelectItem value='facebook'>
                  <div className="flex items-center space-x-3">
                    <IconFacebook className="text-blue-600 w-6 h-6" />
                    <span>Facebook</span>
                  </div>
                </SelectItem>
                <SelectItem value='twitter'>
                  <div className="flex items-center space-x-3">
                    <IconTwitter className="text-blue-400 w-6 h-6" />
                    <span>Twitter</span>
                  </div>
                </SelectItem>
                <SelectItem value='instagram'>
                  <div className="flex items-center space-x-3">
                    <IconInstagram className="text-pink-500 w-6 h-6" />
                    <span>Instagram</span>
                  </div>
                </SelectItem>
                <SelectItem value='tiktok'>
                  <div className="flex items-center space-x-3">
                    <IconTiktok className="text-black w-6 h-6" />
                    <span>TikTok</span>
                  </div>
                </SelectItem>
                <SelectItem value='pinterest'>
                  <div className="flex items-center space-x-3">
                    <IconPinterest className="text-red-500 w-6 h-6" />
                    <span>Pinterest</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div> */}
          {(isLoading || isLinkedInSearching) && (
            <div className="mt-4 text-center text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <IconSpinner className="animate-spin h-5 w-5 mr-2" />
              {connectionStatus === 'connected' && searchProgress ? (
                searchProgress.message || 'Searching...'
              ) : (
                searchMessages[searchMessageIndex]
              )}
            </div>
          )}
          {isSyncing && (
            <div className="mt-4 text-center text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <IconSpinner className="animate-spin h-5 w-5 mr-2" />
              {syncStatus || "Creating your mixus profile..."}
            </div>
          )}
          {!isLoading && !isLinkedInSearching && !isSyncing && searchResultsByPlatform[platform]?.length > 0 && (
            <div className="mt-4">
              {/* <h3 className="font-semibold mb-2 dark:text-white">Search Results:</h3> */}
              <div className="space-y-2">
                {searchResultsByPlatform[platform].map((result, index) => (
                  <div
                    key={index}
                    className="p-2 border rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-3"
                    onClick={() => {
                      setSelectedAlternativeSocialProfile(result);
                      handleSyncProfile(result.url || '', platform);
                    }}
                  >
                    {result.profilePicUrl && (
                      <Image
                        src={result.profilePicUrl}
                        alt={result.title || 'Profile picture'}
                        width={48}
                        height={48}
                        className="rounded-full"
                      />
                    )}
                    <div className="flex-grow">
                      <p className="font-medium text-sm dark:text-white">{result.title}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-300">{result.description}</p>
                      {/* <p className="text-xs text-gray-500 dark:text-gray-400">{platform}</p> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isLoading && !isLinkedInSearching && !isSyncing && searchInitiated &&
            (searchResultsByPlatform[platform]?.length === 0 || searchResultsByPlatform[platform] === undefined) && (
            <div className="mt-4 text-center text-gray-500">
              No results found
            </div>
          )}
        </div>
      )
    },
    {
      title: 'mixus profile',
      description: 'Complete your professional profile to enhance collaboration, knowledge sharing, and networking within your organization.',
      content: (
        <div className="space-y-4">
          {profilePicture && (
            <div className="flex flex-col justify-center items-center w-full h-auto">
              <div className="relative w-auto h-full self-center" onClick={() => fileRef.current?.click()}>
                <ProfilePic
                  imageUrl={`${profilePicture}?height=256&width=256&fit=scale-down&quality=100`}
                  altText={`${fullName} profile picture`}
                  size="w-28 h-28"
                  priority={true}
                  className="cursor-pointer"
                />
                <div className="absolute bottom-0 right-0 bg-white dark:bg-gray-800 rounded-full p-1 shadow-md cursor-pointer">
                  <IconEdit className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </div>
                {savingProfilePic && (
                  <div className="absolute inset-0 bg-muted/40 flex justify-center items-center rounded-full">
                    <IconSpinner className="animate-spin" />
                  </div>
                )}
              </div>
              <input
                type="file"
                onChange={handleUpdateProfilePic}
                hidden
                multiple
                accept="image/*"
                ref={fileRef}
              />
            </div>
          )}
          <div className="space-y-2">
            <label htmlFor="headline" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Headline
            </label>
            <input
              id="headline"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="Enter your headline"
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex space-x-2">
            <input
              value={location.city}
              onChange={(e) => setLocation(prev => ({ ...prev, city: e.target.value }))}
              placeholder="City"
              className="w-1/3 px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
            />
            <input
              value={location.state}
              onChange={(e) => setLocation(prev => ({ ...prev, state: e.target.value }))}
              placeholder="State"
              className="w-1/3 px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
            />
            <input
              value={location.country}
              onChange={(e) => setLocation(prev => ({ ...prev, country: e.target.value }))}
              placeholder="Country"
              className="w-1/3 px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
            />
          </div>
          <input
            value={employer}
            onChange={(e) => setEmployer(e.target.value)}
            placeholder="Work experience"
            className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
          />
          <input
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder="Education"
            className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
          />
          <div>
            <div className="flex justify-between">
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                Places you know well
              </label>
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <IconInfo className="text-gray-500 hover:text-gray-700 size-4" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="z-50 text-center data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none rounded-[4px] bg-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity] text-gray-800 dark:bg-gray-800 dark:text-white">
                  Could be places you lived or traveled <br /> - used to connect you with users inquiring about these places
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <input
              value={placesKnownWell.join(', ')}
              onChange={e =>
                setPlacesKnownWell([e.target.value])
              }
              onBlur={(e) => {
                setPlacesKnownWell(e.target.value.split(',').map(item => item.trim()))
              }}
              placeholder="e.g., New Orleans, Paris, Lake Tahoe"
              className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
            />
          </div>
          <textarea
            value={generatedAboutMe}
            onChange={(e) => setGeneratedAboutMe(e.target.value)}
            placeholder="Tell us about yourself..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:text-white"
            rows={6}
          />
          <Dialog open={showModerationDialog} onOpenChange={setShowModerationDialog}>
            <ContentModerationDialog
              title="Content Review Required"
              validationType="inappropriate_content"
              customMessage={moderationError}
              onEdit={() => setShowModerationDialog(false)}
              editButtonText="Edit Profile"
              onViewSuggestions={() => {}}
              hasSuggestions={false}
              showSuggestionsButton={false}
            />
          </Dialog>
        </div>
      )
    },
    // {
    //   title: 'Build out your mixus network',
    //   description: 'Tell your contacts about mixus. Help grow the mixus organization by inviting your friends and colleagues!',
    //   content: (
    //     <div className="space-y-4">
    //       <div className="flex justify-center space-x-4">
    //         <Button
    //           onClick={shareOnFacebook}
    //           className="flex items-center space-x-3 px-6 py-2 rounded-full bg-sky-200"
    //         >
    //           <div className="w-8 h-8 flex items-center justify-center">
    //             <IconFacebook className="text-blue-600 w-6 h-6" />
    //           </div>
    //           <span>Facebook</span>
    //         </Button>
    //         <Button
    //           onClick={shareOnX}
    //           className="flex items-center space-x-3 px-6 py-2 rounded-full bg-sky-200"
    //         >
    //           <div className="w-8 h-8 flex items-center justify-center">
    //             <IconTwitter className="text-blue-400 w-6 h-6" />
    //           </div>
    //           <span>Twitter</span>
    //         </Button>
    //         <Button
    //           onClick={shareOnLinkedIn}
    //           className="flex items-center space-x-3 px-6 py-2 rounded-full bg-sky-200"
    //         >
    //           <div className="w-8 h-8 flex items-center justify-center">
    //             <IconLinkedin className="text-blue-700 w-6 h-6" />
    //           </div>
    //           <span>LinkedIn</span>
    //         </Button>
    //       </div>
    //     </div>
    //   )
    // }
  ];

  useEffect(() => {
    const handleReferral = async () => {
      if (referral && mixusUserId && mixusUsername) {
        try {
          const referralUsername = referral.replace(/^user_/, '');
          if (referralUsername === mixusUsername) {
            logger.info(`instructions: user ${mixusUsername} can't be referred by themselves`)
            return;
          }
          if (mixusUserReferredBy) {
            logger.info(`instructions: user ${mixusUsername} was already referred by ${mixusUserReferredBy} and can't be referred again by ${referral}`)
            return;
          }
          await updateUser(mixusUserId, 'referredBy', referral);
          logger.info('Referral saved successfully');

          // Check if the response indicates old & new referral is same. If it is, don't notify the referrer.
          await notifyReferrer(referral, mixusUserFullName, mixusUserId);
        } catch (error) {
          logger.error('Error saving referral:', error);
        }
      }
    };

    handleReferral();
  }, [mixusUserId, referral, mixusUsername]);

  const handleButtonClick = () => {
    if (currentStep === 2 && alternativeSocialUrl) {
      const platform = getPlatformFromUrl(alternativeSocialUrl);
      if (platform === 'unknown') {
        toast.error('Please enter a valid LinkedIn, Twitter, Instagram, Facebook, TikTok, or Pinterest URL');
        return;
      }
      handleSyncProfile(alternativeSocialUrl, platform);
    } else if (currentStep === 2 && !alternativeSocialUrl) {
      setHasSocialMedia(false);
      nextStep();
    } else {
      nextStep();
    }
  };

  if (syncingBg) {
    return null;
  }

  /* 
   * ONBOARDING DIALOG TEMPORARILY DISABLED
   * 
   * This component is kept mounted because it handles:
   * Referral tracking and processing (ref URL handling, user updates, referrer notifications)

   * To re-enable the onboarding dialog:
   * 1. Remove the early "return null" below
   * 2. Uncomment the setShowOnboarding(true) call in the useEffect above
   * 3. Uncomment the JSX code below
   */
  return null;

  /* Original onboarding dialog UI
  if (!showOnboarding) {
    return null;
  }

  return (
    <>
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/50 dark:bg-white/10 z-50"
        onClick={handleOutsideClick}
      >
        <div className="w-full max-h-[90vh] max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-md pt-6 px-6 m-4 overflow-y-auto">
          <h2 className="text-2xl font-bold mb-4 dark:text-white">{steps[currentStep].title}</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{steps[currentStep].description}</p>
          {steps[currentStep].content}
          <div className="mt-6 flex justify-between sticky bottom-0 bg-white dark:bg-gray-800 py-4 border-t">
            {currentStep > 0 && (
              <Button onClick={() => setCurrentStep(currentStep - 1)} variant="outline">
                Back
              </Button>
            )}
            <Button
              onClick={handleButtonClick}
              disabled={(currentStep === 2 && hasSocialMedia && alternativeSocialUrl && !selectedAlternativeSocialProfile) || isSaving || isSyncing}
            >
              {currentStep === steps.length - 1 ? (
                <>
                  {isSaving ? (
                    <>
                      <IconSpinner className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Finish'
                  )}
                </>
              ) : currentStep === 2 && alternativeSocialUrl ? (
                'Create Profile'
              ) : currentStep === 2 && !alternativeSocialUrl ? (
                'Skip'
              ) : (
                'Next'
              )}
            </Button>
          </div>
        </div>
        <Dialog open={showModerationDialog} onOpenChange={setShowModerationDialog}>
          <ContentModerationDialog
            title="Content Review Required"
            validationType="inappropriate_content"
            customMessage={moderationError}
            onEdit={() => setShowModerationDialog(false)}
            editButtonText="Edit Profile"
            onViewSuggestions={() => {}}
            hasSuggestions={false}
            showSuggestionsButton={false}
          />
        </Dialog>
      </div>
      <DemoRequestDialog
        open={showDemoDialog}
        onOpenChange={setShowDemoDialog}
      />
    </>
  );
  */
};

export default Onboarding;
